import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import CustomModal from "../../../components/modal/CustomModal";
import { bibleApi } from "../bible-api";
import { BibleImportData, Language } from "../types";

interface ImportBibleVersionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ImportBibleVersionModal({
  open,
  onClose,
  onSuccess,
}: ImportBibleVersionModalProps) {
  const [formData, setFormData] = useState<BibleImportData>({
    version_name: "",
    language_code: "",
    // languageCode: "",
    description: "",
    abbreviation: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [structuredData, setStructuredData] = useState<string>("");
  const [importType, setImportType] = useState<"file" | "structured">("file");
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [update, setUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      fetchLanguages();
    }
  }, [open]);

  const fetchLanguages = async () => {
    try {
      // const langs = await bibleApi.getLanguages();
      // setLanguages(langs);
      setLanguages([
        { code: "en", name: "English" },
        { code: "es", name: "Spanish" },
        { code: "fr", name: "French" },
        { code: "de", name: "German" },
        { code: "it", name: "Italian" },
        { code: 'ml', name: 'Malayalam' },
        { code: 'ta', name: 'Tamil' },
        { code: 'hi', name: 'Hindi' },
        { code: 'el', name: 'Greek' },
        ]);
    } catch (error) {
      console.error("Failed to fetch languages:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLanguage = languages.find((lang) => lang.code === e.target.value);
    if (selectedLanguage) {
      setFormData((prev) => ({
        ...prev,
        language_code: selectedLanguage.code,
        // languageCode: selectedLanguage.code,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const importData: BibleImportData = {
        ...formData,
        file: importType === "file" ? file || undefined : undefined,
        structuredData: importType === "structured" ? structuredData : undefined,
      };

      await bibleApi.importVersion(importData);
      toast.success("Bible version imported successfully!");
      onSuccess();
      handleClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to import Bible version");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      version_name: "",
      language_code: "",
      // languageCode: "",
      description: "",
      abbreviation: "",
    });
    setFile(null);
    setStructuredData("");
    setImportType("file");
    setUpdate(false);
    onClose();
  };

  return (
    <CustomModal open={open} onClose={handleClose} className="max-w-2xl">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Import Bible Version
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Version Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="version_name"
              value={formData.version_name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., King James Version"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Language <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.language_code}
              onChange={handleLanguageChange}
              required
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a language</option>
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Abbreviation
            </label>
            <input
              type="text"
              name="abbreviation"
              value={formData.abbreviation}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., KJV"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional description of this Bible version"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Import Type
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="file"
                  checked={importType === "file"}
                  onChange={(e) => setImportType(e.target.value as "file" | "structured")}
                  className="mr-2"
                />
                <span className="text-gray-700 dark:text-gray-300">File Upload</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="structured"
                  checked={importType === "structured"}
                  onChange={(e) => setImportType(e.target.value as "file" | "structured")}
                  className="mr-2"
                />
                <span className="text-gray-700 dark:text-gray-300">Structured Input</span>
              </label>
            </div>
          </div>

          {importType === "file" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bible File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                required={importType === "file"}
                accept=".db"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Supported formats: .db (sqlite)
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Structured Data (JSON) <span className="text-red-500">*</span>
              </label>
              <textarea
                value={structuredData}
                onChange={(e) => setStructuredData(e.target.value)}
                required={importType === "structured"}
                rows={8}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder='{"books": [{"name": "Genesis", "chapters": [...]}]}'
              />
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={update}
                onChange={(e) => setUpdate(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Update</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Importing...
                </span>
              ) : (
                "Import Version"
              )}
            </button>
          </div>
        </form>
      </div>
    </CustomModal>
  );
}

